import React, {useState} from 'react';
import {Show, useRecordContext, useRedirect, useTranslate, usePermissions} from 'react-admin';
import {Link as RouterLink} from 'react-router-dom';
import {Alert, Avatar, Box, Link as MuiLink, Typography} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {DeleteOutline, EditOutlined} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import DataRow from '../components/statamic/DataRow';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import {axiosApiInstance} from '../AxiosUtils';
import hasPermission from '../auth/hasPermission';

const formatDate = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

const formatDateTime = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const ROLE_LABELS = {
    UMBRELLA_ADMIN:    'Umbrella Admin',
    CHARITY_ADMIN:     'Charity Admin',
    CHARITY_VOLUNTEER: 'Charity Volunteer',
};

const MetaRow = ({label, children}) => (
    <Box sx={{display: 'grid', gridTemplateColumns: '160px 1fr', alignItems: 'baseline', gap: 1.5, py: 0.5}}>
        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontWeight: 500}}>
            {label}
        </Typography>
        <Box sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, minWidth: 0}}>
            {children || <Box component="span" sx={{color: t.color.text.muted}}>—</Box>}
        </Box>
    </Box>
);

const VolunteerHero = ({record, translate}) => {
    const avatarUrl = record.avatar ? `${process.env.REACT_APP_CDN_URL}/${record.avatar}` : undefined;
    // v2 volunteer model carries no `salutation` field — render the name without a prefix.
    const fullName = [record.given_name, record.family_name].filter(Boolean).join(' ') || 'Unnamed';
    const isUmbrellaLevel = record.charity?.umbrella;
    const roleLabel = record.realm_role ? ROLE_LABELS[record.realm_role] || record.realm_role : null;

    return (
        <Box
            sx={{
                backgroundColor: t.color.surface,
                border: `1px solid ${t.color.border.default}`,
                borderRadius: `${t.radius.md}px`,
                p: 3,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 4,
                alignItems: 'stretch',
            }}
        >
            <Box sx={{display: 'flex', gap: 3, alignItems: 'center', minWidth: 0}}>
                <Avatar
                    src={avatarUrl}
                    sx={{
                        width: 96,
                        height: 96,
                        fontSize: 32,
                        flexShrink: 0,
                        bgcolor: t.color.sidebarHover,
                        color: t.color.text.secondary,
                        border: `1px solid ${t.color.border.hairline}`,
                    }}
                >
                    {(record.given_name?.charAt(0) ?? '') + (record.family_name?.charAt(0) ?? '')}
                </Avatar>
                <Box sx={{minWidth: 0, flex: 1}}>
                    <Typography
                        sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: t.color.text.primary,
                            lineHeight: 1.2,
                            wordBreak: 'break-word',
                        }}
                    >
                        {fullName}
                    </Typography>
                    <Box sx={{display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap'}}>
                        <StatusPill variant={record.enabled ? 'success' : 'neutral'}>
                            {record.enabled ? translate('Enabled') : translate('Disabled')}
                        </StatusPill>
                        {roleLabel && (
                            <StatusPill variant="neutral">{roleLabel}</StatusPill>
                        )}
                        {isUmbrellaLevel && (
                            <StatusPill variant="info">Umbrella</StatusPill>
                        )}
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    pl: 3,
                    borderLeft: `1px solid ${t.color.border.hairline}`,
                }}
            >
                <MetaRow label={translate('Email')}>
                    {record.email ? (
                        <MuiLink
                            href={`mailto:${record.email}`}
                            sx={{color: t.color.accent.primary, textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
                        >
                            {record.email}
                        </MuiLink>
                    ) : null}
                </MetaRow>
                <MetaRow label={translate(isUmbrellaLevel ? 'Umbrella' : 'Charity')}>
                    {record.charity?.name}
                </MetaRow>
                <MetaRow label={translate('Registration date')}>
                    {formatDate(record.create_date_time)}
                </MetaRow>
                <MetaRow label={translate('Last access date')}>
                    {formatDateTime(record.last_access_date_time)}
                </MetaRow>
            </Box>
        </Box>
    );
};

const ResetPasswordAction = ({record, permissions, translate}) => {
    const [resetting, setResetting] = useState(false);
    const [status, setStatus] = useState(null);

    const resetPassword = () => {
        setResetting(true);
        setStatus(null);
        const rolePath = permissions === 'admin' ? 'admin' : 'umbrellad';
        axiosApiInstance.post(
            `${process.env.REACT_APP_API_URL}/api/v2/${rolePath}/volunteers/${record.id}/resetPassword`
        ).then(() => {
            setResetting(false);
            setStatus('success');
        }).catch(() => {
            setResetting(false);
            setStatus('error');
        });
    };

    return (
        <Box>
            <LoadingButton
                onClick={resetPassword}
                loading={resetting}
                variant="outlined"
                size="small"
                sx={{
                    textTransform: 'none',
                    fontSize: `${t.type.small.size}px`,
                    borderColor: t.color.border.default,
                    color: t.color.text.primary,
                    borderRadius: `${t.radius.sm}px`,
                    '&:hover': {borderColor: t.color.accent.primary, color: t.color.accent.primary, backgroundColor: 'transparent'},
                }}
            >
                {translate('Reset password')}
            </LoadingButton>
            {status === 'success' && (
                <Alert severity="success" sx={{mt: 1, fontSize: `${t.type.small.size}px`}}>
                    {translate('Password reset mail sent')}
                </Alert>
            )}
            {status === 'error' && (
                <Alert severity="warning" sx={{mt: 1, fontSize: `${t.type.small.size}px`}}>
                    {translate('Could not send password reset mail')}
                </Alert>
            )}
        </Box>
    );
};

const CharityChip = ({charityExternalId, charityName}) => (
    <Box
        component={charityExternalId ? RouterLink : 'span'}
        to={charityExternalId ? `/charities/${charityExternalId}/show` : undefined}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1,
            py: 0.25,
            borderRadius: `${t.radius.sm}px`,
            border: `1px solid ${t.color.border.default}`,
            backgroundColor: t.color.surface,
            fontSize: `${t.type.caption.size}px`,
            color: t.color.text.primary,
            textDecoration: 'none',
            mr: 0.5,
            mb: 0.5,
            '&:hover': charityExternalId ? {borderColor: t.color.accent.primary, color: t.color.accent.primary} : undefined,
        }}
    >
        {charityName}
    </Box>
);

const VolunteerShowContent = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const deleteConfirm = useDeleteConfirm();

    if (!record) return null;

    const canEdit = hasPermission(permissions, ['edit'], 'volunteers');
    const canDelete = hasPermission(permissions, ['delete'], 'volunteers');

    // v2 volunteer model carries no `salutation` field — render the name without a prefix.
    const fullName = [record.given_name, record.family_name].filter(Boolean).join(' ') || 'Unnamed';

    const isUmbrellaLevel = record.charity?.umbrella;
    // v2 rows carry `charities: [{external_id, name}]` — no `charity_ids`/numeric lookup.
    const charities = Array.isArray(record.charities) ? record.charities : [];

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={{label: translate('Volunteers'), to: '/volunteers'}}
                title={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={record.enabled ? 'success' : 'neutral'}/>
                        <Box component="span">{fullName}</Box>
                    </Box>
                }
                actions={
                    <>
                        {canEdit && (
                            <PrimaryButton
                                variant="primary"
                                startIcon={<EditOutlined sx={{fontSize: 16}}/>}
                                onClick={() => redirect('edit', 'volunteers', record.id)}
                            >
                                {translate('Edit')}
                            </PrimaryButton>
                        )}
                        {canDelete && (
                            <PrimaryButton
                                variant="danger"
                                startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                                onClick={deleteConfirm.openDialog}
                            >
                                {translate('Delete')}
                            </PrimaryButton>
                        )}
                    </>
                }
            />

            <Box sx={{p: 3, display: 'flex', flexDirection: 'column', gap: 2}}>
                <VolunteerHero record={record} translate={translate}/>

                <SectionCard
                    title={translate('Contact details')}
                    actions={<ResetPasswordAction record={record} permissions={permissions} translate={translate}/>}
                >
                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                        <DataRow label={translate('Email')} value={record.email}/>
                    </Box>
                </SectionCard>

                <SectionCard title={translate('Coordinating')}>
                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                        <DataRow label={translate('Role')}>
                            {record.realm_role
                                ? (ROLE_LABELS[record.realm_role] || record.realm_role)
                                : null}
                        </DataRow>
                        <DataRow label={translate(isUmbrellaLevel ? 'Umbrella' : 'Charity')}
                                 value={record.charity?.name}/>
                    </Box>
                    {!isUmbrellaLevel && charities.length > 0 && (
                        <Box sx={{mt: 1, pt: 1, borderTop: `1px solid ${t.color.border.hairline}`}}>
                            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontWeight: 500, mb: 0.75}}>
                                {translate('Projects')}
                            </Typography>
                            <Box sx={{display: 'flex', flexWrap: 'wrap'}}>
                                {charities.map((c) => (
                                    <CharityChip
                                        key={c.external_id}
                                        charityExternalId={c.external_id}
                                        charityName={c.name}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </SectionCard>
            </Box>

            <DeleteConfirmDialog
                open={deleteConfirm.open}
                onClose={deleteConfirm.closeDialog}
                resource="volunteers"
                record={record}
                recordLabel={fullName}
            />
        </Box>
    );
};

const VolunteerShowStatamic = (props) => (
    <Show {...props} actions={false} component={Box} sx={{backgroundColor: t.color.page}}>
        <VolunteerShowContent/>
    </Show>
);

export default VolunteerShowStatamic;
